import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Check, Shield, Star, Upload, Plus, Trash2, Award } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { validateFile, sanitizeFileName } from '@/utils/fileUploadSecurity';

// Education entry type
type EducationEntry = {
  degree: string;
  institution: string;
  year: string;
};

// Form schema
const caregiverApplicationSchema = z.object({
  // Step 1: Verification Path
  verificationPath: z.enum(['basic', 'trusted_pro']),
  
  // Step 2: Profile Building
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  preferredName: z.string().optional(),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  companyName: z.string().optional(),
  website: z.string().url().optional().or(z.literal('')),
  bio: z.string().min(50, 'Bio must be at least 50 characters'),
  bioShort: z.string().min(10, 'Short bio must be at least 10 characters'),
  professionalPhoto: z.string().optional(),
  experienceYears: z.string(),
  hourlyRate: z.string().optional(),

  // Education & Credentials (Enhanced)
  educationLevel: z.enum(['high_school','ged','some_college','associates','bachelors','masters','nursing','other']),
  educationList: z.array(z.object({
    degree: z.string().min(1, 'Degree is required'),
    institution: z.string().min(1, 'Institution is required'),
    year: z.string().min(4, 'Year is required')
  })).optional().default([]),
  educationDetails: z.string().optional().or(z.literal('')),
  
  // Languages (Structured)
  languages: z.array(z.string()).min(1, 'Please select at least one language'),
  
  // Vaccinations
  vaccinationStatus: z.enum(['up_to_date','partial','not_current','prefer_not_to_say']),
  vaccinationsCurrent: z.array(z.string()).optional().default([]),
  vaccinationsOther: z.string().optional().or(z.literal('')),
  
  // Professional Credentials
  professionalCredentials: z.array(z.string()).optional().default([]),
  credentialsOther: z.string().optional().or(z.literal('')),
  
  // Certifications
  certifications: z.array(z.string()).optional().default([]),
  
  // Step 3: Skills & Services
  skillsAdlIadl: z.array(z.string()).min(1, 'Please select at least one service'),
  skillsDementiaCare: z.boolean(),
  skillsMobilityAssistance: z.boolean(),
  skillsMedicationSupport: z.boolean(),
  services: z.array(z.string()).min(1, 'Please select at least one service'),
  comfort: z.array(z.string()).optional().default([]),
  hasTransportation: z.boolean(),
  comfortableWithPets: z.boolean(),
  willingHousekeeping: z.boolean(),
  
  // Step 4: Availability & Service Area
  caregiverType: z.enum(['independent', 'agency']),
  serviceCity: z.string().min(2, 'Please enter your service city'),
  serviceZipCode: z.string().min(5, 'Please enter a valid zip code'),
  serviceRadius: z.number().min(1).max(50),
  availabilityDays: z.array(z.string()).min(1, 'Please select at least one day'),
  availabilityHours: z.enum(['full-time', 'part-time', 'flexible']),
  overnight: z.boolean().optional().default(false),
  startDate: z.string().optional(),
  maxWeeklyHours: z.string().optional(),
  preferredShift: z.string().optional(),
  
  // Step 5: Agreement
  termsAgreed: z.boolean().refine(val => val === true, 'You must agree to the terms'),
  backgroundCheckConsent: z.boolean(),
  
  // Tools for competitive intelligence
  toolsUsed: z.array(z.string()).optional().default([]),
  toolsOther: z.string().optional().or(z.literal('')),
});

type FormData = z.infer<typeof caregiverApplicationSchema>;

const STEPS = [
  { id: 1, title: 'Choose membership', description: 'Select your verification path' },
  { id: 2, title: 'Build your profile', description: 'Tell us about yourself and your background' },
  { id: 3, title: 'Services & skills', description: 'What you offer and your expertise' },
  { id: 4, title: 'Availability & area', description: 'Your schedule and service location' },
  { id: 5, title: 'Review and submit', description: 'Final review of your application' },
];

const SERVICE_OPTIONS = [
  'Personal care',
  'Medication reminders',
  'Meal preparation',
  'Light housekeeping',
  'Companionship',
  'Transportation',
  'Mobility support',
  'Memory care',
  'Grocery shopping',
  'Appointment assistance'
];

const COMFORT_OPTIONS = [
  'Pets in home',
  'Scent sensitivities',
  'Smoke in home'
];

const CERTIFICATION_OPTIONS = [
  'CNA',
  'HHA', 
  'CPR',
  'First Aid',
  'Dementia Care',
  'Medication Assistance'
];

const AVAILABILITY_DAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

const VACCINE_OPTIONS = [
  'COVID-19','Flu','Hepatitis B','Pneumonia','Shingles'
];

const CREDENTIAL_OPTIONS = [
  'HHA (Home Health Aide)','PCA (Personal Care Aide)','First Aid','BLS','ACLS','Food Handler'
];

const TOOLS_OPTIONS = [
  // Everyday Tools
  { label: 'Pen and Paper', value: 'pen_paper', tag: 'tool_pen_paper', category: '📝 Everyday Tools' },
  { label: 'Notes App on Phone', value: 'phone_notes', tag: 'tool_phone_notes', category: '📝 Everyday Tools' },
  { label: 'Text Messaging', value: 'text_msg', tag: 'tool_text_msg', category: '📝 Everyday Tools' },
  { label: 'Google Calendar / Cozi', value: 'calendar_general', tag: 'tool_calendar_general', category: '📝 Everyday Tools' },
  
  // Professional Caregiver Platforms
  { label: 'WellSky (ClearCare)', value: 'wellsky', tag: 'tool_wellsky', category: '💼 Professional Caregiver Platforms' },
  { label: 'AxisCare', value: 'axiscare', tag: 'tool_axiscare', category: '💼 Professional Caregiver Platforms' },
  { label: 'AlayaCare', value: 'alayacare', tag: 'tool_alayacare', category: '💼 Professional Caregiver Platforms' },
  { label: 'Axxess', value: 'axxess', tag: 'tool_axxess', category: '💼 Professional Caregiver Platforms' },
  { label: 'Smartcare (Aaniie)', value: 'smartcare', tag: 'tool_smartcare', category: '💼 Professional Caregiver Platforms' },
  { label: 'eRSP', value: 'ersp', tag: 'tool_ersp', category: '💼 Professional Caregiver Platforms' },
  { label: 'Homecare Homebase (HCHB)', value: 'hchb', tag: 'tool_hchb', category: '💼 Professional Caregiver Platforms' },
  { label: 'CareLinx', value: 'carelinx', tag: 'tool_carelinx', category: '💼 Professional Caregiver Platforms' },
  { label: 'Care.com', value: 'care_dot_com', tag: 'tool_care_dot_com', category: '💼 Professional Caregiver Platforms' },
  
  // Family Caregiver Apps
  { label: 'Caring Village', value: 'caringvillage', tag: 'tool_caringvillage', category: '🏡 Family Caregiver Apps' },
  { label: 'Lotsa Helping Hands', value: 'lotsa', tag: 'tool_lotsa', category: '🏡 Family Caregiver Apps' },
  { label: 'CaringBridge', value: 'caringbridge', tag: 'tool_caringbridge', category: '🏡 Family Caregiver Apps' },
  { label: 'CareZone', value: 'carezone', tag: 'tool_carezone', category: '🏡 Family Caregiver Apps' },
  { label: 'Medisafe', value: 'medisafe', tag: 'tool_medisafe', category: '🏡 Family Caregiver Apps' },
  { label: 'ianacare', value: 'ianacare', tag: 'tool_ianacare', category: '🏡 Family Caregiver Apps' },
  { label: 'Carely', value: 'carely', tag: 'tool_carely', category: '🏡 Family Caregiver Apps' },
  { label: 'CircleOf', value: 'circleof', tag: 'tool_circleof', category: '🏡 Family Caregiver Apps' },
  { label: 'WayWiser', value: 'waywiser', tag: 'tool_waywiser', category: '🏡 Family Caregiver Apps' },
  
  // Other/None
  { label: 'Other (please specify)', value: 'other', tag: 'tool_other', category: '❓ Other or None' },
  { label: 'None', value: 'none', tag: 'tool_none', category: '❓ Other or None' },
];

const EXPERIENCE_OPTIONS = [
  '0 to 1',
  '2 to 4', 
  '5 to 9',
  '10 plus'
];

const EDUCATION_LEVELS = [
  { value: 'high_school', label: 'High school or GED' },
  { value: 'some_college', label: 'Some college' },
  { value: 'associates', label: 'Associate degree' },
  { value: 'bachelors', label: 'Bachelor\'s degree' },
  { value: 'masters', label: 'Master\'s degree' },
  { value: 'nursing', label: 'Nursing program' },
  { value: 'other', label: 'Other' }
];

const LANGUAGE_OPTIONS = [
  'English',
  'Spanish',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Russian',
  'Chinese (Mandarin)',
  'Chinese (Cantonese)',
  'Japanese',
  'Korean',
  'Vietnamese',
  'Thai',
  'Hindi',
  'Arabic',
  'Hebrew',
  'Polish',
  'Dutch',
  'Swedish',
  'Norwegian',
  'Danish',
  'Finnish',
  'Greek',
  'Turkish',
  'Tagalog',
  'Other'
];

const HOURLY_RATE_OPTIONS = [
  { value: '$15-20', label: '$15 - $20 per hour' },
  { value: '$21-25', label: '$21 - $25 per hour' },
  { value: '$26-30', label: '$26 - $30 per hour' },
  { value: '$31-35', label: '$31 - $35 per hour' },
  { value: '$36-40', label: '$36 - $40 per hour' },
  { value: '$41-45', label: '$41 - $45 per hour' },
  { value: '$46-50', label: '$46 - $50 per hour' },
  { value: '$50+', label: '$50+ per hour' },
  { value: 'negotiable', label: 'Negotiable' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' }
];

const LICENSE_TYPES = [
  { value: '', label: 'None' },
  { value: 'CNA', label: 'CNA' },
  { value: 'HHA', label: 'HHA' },
  { value: 'LPN', label: 'LPN' },
  { value: 'LVN', label: 'LVN' },
  { value: 'RN', label: 'RN' },
  { value: 'Other', label: 'Other' }
];

interface CaregiverApplicationFormProps {
  verificationPath?: 'basic' | 'trusted_pro';
}

export default function CaregiverApplicationForm({ verificationPath }: CaregiverApplicationFormProps = {}) {
  // Skip step 1 if verificationPath is provided (membership already selected)
  const [currentStep, setCurrentStep] = useState(verificationPath ? 2 : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
const form = useForm<FormData>({
  resolver: zodResolver(caregiverApplicationSchema),
  defaultValues: {
    verificationPath: verificationPath || 'basic',
    // Profile
    preferredName: '',
    bioShort: '',
    experienceYears: '0 to 1',
    hourlyRate: '',
    
    // Education & Credentials
    educationLevel: 'high_school',
    educationList: [],
    educationDetails: '',
    languages: [],
    vaccinationStatus: 'prefer_not_to_say',
    vaccinationsCurrent: [],
    vaccinationsOther: '',
    professionalCredentials: [],
    credentialsOther: '',
    certifications: [],
    
    // Skills & Services
    skillsAdlIadl: [],
    skillsDementiaCare: false,
    skillsMobilityAssistance: false,
    skillsMedicationSupport: false,
    services: [],
    comfort: [],
    hasTransportation: false,
    comfortableWithPets: false,
    willingHousekeeping: false,
    
    // Availability
    caregiverType: 'independent',
    serviceRadius: 25,
    availabilityDays: [],
    availabilityHours: 'flexible',
    overnight: false,
    startDate: '',
    maxWeeklyHours: '',
    preferredShift: '4 to 6 hours',
    
    // Agreement
    termsAgreed: false,
    backgroundCheckConsent: false,
    
    // Tools
    toolsUsed: [],
    toolsOther: '',
  },
});

  const watchedValues = form.watch();
  const isTrustedPro = watchedValues.verificationPath === 'trusted_pro';
  
  // Adjust progress calculation based on whether we skip step 1
  const totalSteps = verificationPath ? STEPS.length - 1 : STEPS.length;
  const adjustedStep = verificationPath ? currentStep - 1 : currentStep;
  const progress = (adjustedStep / totalSteps) * 100;

  const nextStep = async () => {
    const fieldsToValidate = getFieldsForStep(currentStep);
    const isValid = await form.trigger(fieldsToValidate);
    
    if (isValid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const prevStep = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const minStep = verificationPath ? 2 : 1;
    setCurrentStep(prev => Math.max(prev - 1, minStep));
  };

  const handleMembershipSelection = async (path: 'basic' | 'trusted_pro') => {
    form.setValue('verificationPath', path);
    // Wait a brief moment for the form to update, then advance
    setTimeout(() => {
      nextStep();
    }, 100);
  };

const getFieldsForStep = (step: number): (keyof FormData)[] => {
  switch (step) {
    case 1: return ['verificationPath'];
    case 2: return ['firstName', 'lastName', 'email', 'phone', 'bioShort', 'educationLevel', 'languages'];
    case 3: return ['services', 'experienceYears'];
    case 4: return ['caregiverType', 'serviceCity', 'serviceZipCode', 'availabilityDays'];
    case 5: return ['termsAgreed'];
    default: return [];
  }
};

const onSubmit = async (data: FormData) => {
  console.log('Form submission started', { data, termsAgreed: data.termsAgreed });
  setIsSubmitting(true);
  
  try {
    const languagesList = data.languages || [];
    const vaccinationsCombined = data.vaccinationsOther ? [...(data.vaccinationsCurrent || []), data.vaccinationsOther] : (data.vaccinationsCurrent || []);
    const credentialsCombined = data.credentialsOther ? [...(data.professionalCredentials || []), data.credentialsOther] : (data.professionalCredentials || []);

    const submissionData = {
      profession: data.caregiverType === 'agency' ? 'Home Care Agency' : 'Independent Caregiver',
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      bio: data.bio || data.bioShort,
      bioShort: data.bioShort,
      preferredName: data.preferredName,
      professionalPhotoUrl: data.professionalPhoto,
      experienceYears: data.experienceYears,
      hourlyRateRange: data.hourlyRate,
      serviceCity: data.serviceCity,
      serviceZipCode: data.serviceZipCode,
      serviceRadiusMiles: data.serviceRadius,
      termsAgreed: data.termsAgreed,
      
      // Caregiver-specific fields
      caregiverType: data.caregiverType,
      verificationStatus: data.verificationPath === 'trusted_pro' ? 'trusted_pro' : 'basic_member',
      skillsAdlIadl: data.services,
      skillsDementiaCare: data.skillsDementiaCare,
      skillsMobilityAssistance: data.skillsMobilityAssistance,
      skillsMedicationSupport: data.skillsMedicationSupport,
      certifications: data.certifications,
      hasTransportation: data.hasTransportation,
      comfortableWithPets: data.comfortableWithPets,
      willingHousekeeping: data.willingHousekeeping,
      availabilityDays: data.availabilityDays,
      availabilityHours: data.availabilityHours,
      overnightCare: data.overnight,
      startDate: data.startDate,
      maxWeeklyHours: data.maxWeeklyHours,
      preferredShift: data.preferredShift,
      backgroundCheckStatus: isTrustedPro ? 'not_started' : 'not_started',
      backgroundCheckConsent: data.backgroundCheckConsent,

      // Education, Languages, Vaccinations, Credentials
      educationLevel: data.educationLevel,
      educationDetails: data.educationDetails,
      educationEntries: data.educationList,
      languagesSpoken: languagesList,
      vaccinationsCurrent: vaccinationsCombined,
      vaccinationStatus: data.vaccinationStatus,
      professionalCredentials: credentialsCombined,
      
      // Tools for competitive intelligence
      toolsUsed: data.toolsUsed || [],
      toolsOther: data.toolsOther || '',
      
      // UTM and tracking
      source: 'caregiver-application-form',
      pagePath: window.location.pathname,
    };

    console.log('Submitting caregiver application:', submissionData);

    const { data: result, error } = await supabase.functions.invoke('submit-professional-application', {
      body: submissionData
    });

    if (error) throw error;

    toast.success(
      isTrustedPro 
        ? 'Application submitted! We\'ll contact you about the Trusted Pro verification process.'
        : 'Application submitted successfully! You\'re now a Basic Member of our caregiver network.'
    );
    
    // Track Reddit Lead event for caregiver application
    if (typeof window !== 'undefined' && window.rdt) {
      window.rdt('track', 'Lead', {
        lead_type: 'caregiver_application',
        caregiver_type: 'individual',
        verification_path: isTrustedPro ? 'trusted_pro' : 'basic',
        profession: 'Caregiver',
        source: 'caregiver-application-form',
        page_path: window.location.pathname,
      });
    }
    
    // Reset form or redirect
    form.reset();
    setCurrentStep(verificationPath ? 2 : 1);
    
  } catch (error) {
    console.error('Submission error:', error);
    toast.error('Failed to submit application. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

// Helper functions for managing form arrays
const handleServiceToggle = (service: string, checked: boolean) => {
  const current = form.getValues('services');
  if (checked) {
    form.setValue('services', [...current, service]);
  } else {
    form.setValue('services', current.filter(s => s !== service));
  }
};

const handleComfortToggle = (comfort: string, checked: boolean) => {
  const current = form.getValues('comfort');
  if (checked) {
    form.setValue('comfort', [...current, comfort]);
  } else {
    form.setValue('comfort', current.filter(c => c !== comfort));
  }
};

const handleCertificationToggle = (cert: string, checked: boolean) => {
  const current = form.getValues('certifications');
  if (checked) {
    form.setValue('certifications', [...current, cert]);
  } else {
    form.setValue('certifications', current.filter(c => c !== cert));
  }
};

const handleToolToggle = (toolValue: string, checked: boolean) => {
  const current = form.getValues('toolsUsed') || [];
  if (checked) {
    form.setValue('toolsUsed', [...current, toolValue]);
  } else {
    form.setValue('toolsUsed', current.filter(t => t !== toolValue));
  }
};

const handleDayToggle = (day: string, checked: boolean) => {
  const currentDays = form.getValues('availabilityDays');
  if (checked) {
    form.setValue('availabilityDays', [...currentDays, day]);
  } else {
    form.setValue('availabilityDays', currentDays.filter(d => d !== day));
  }
};

const handleVaccinationToggle = (v: string, checked: boolean) => {
  const current = form.getValues('vaccinationsCurrent') || [];
  if (checked) {
    form.setValue('vaccinationsCurrent', [...current, v]);
  } else {
    form.setValue('vaccinationsCurrent', current.filter(x => x !== v));
  }
};

const handleCredentialToggle = (c: string, checked: boolean) => {
  const current = form.getValues('professionalCredentials') || [];
  if (checked) {
    form.setValue('professionalCredentials', [...current, c]);
  } else {
    form.setValue('professionalCredentials', current.filter(x => x !== c));
  }
};

// Education management
const addEducation = () => {
  const current = form.getValues('educationList');
  form.setValue('educationList', [...current, { degree: '', institution: '', year: '' }]);
};

const removeEducation = (index: number) => {
  const current = form.getValues('educationList');
  form.setValue('educationList', current.filter((_, i) => i !== index));
};

const updateEducation = (index: number, field: keyof EducationEntry, value: string) => {
  const current = form.getValues('educationList');
  const updated = current.map((entry, i) => 
    i === index ? { ...entry, [field]: value } : entry
  );
  form.setValue('educationList', updated);
};

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Progress Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Join as a Caregiver</h1>
          <Badge variant="outline">{verificationPath ? currentStep - 1 : currentStep} of {verificationPath ? STEPS.length - 1 : STEPS.length}</Badge>
        </div>
        <Progress value={progress} className="mb-2" />
        <p className="text-sm text-muted-foreground">
          {STEPS[currentStep - 1].description}
        </p>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
<CardTitle className="flex items-center gap-2">
  {currentStep === 1 && <Shield className="w-5 h-5" />}
  {currentStep === 2 && <Star className="w-5 h-5" />}
  {currentStep === 3 && <Check className="w-5 h-5" />}
  {currentStep === 4 && <Check className="w-5 h-5" />}
  {currentStep === 5 && <Check className="w-5 h-5" />}
  {STEPS[currentStep - 1].title}
</CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Step 1: Verification Path - Only show if no verificationPath provided */}
            {currentStep === 1 && !verificationPath && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold mb-2">Choose Your Membership Level</h3>
                  <p className="text-muted-foreground">
                    Select the level that best fits your caregiving goals
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Basic Member */}
                  <Card 
                    className={`cursor-pointer transition-all hover:shadow-lg ${watchedValues.verificationPath === 'basic' ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => handleMembershipSelection('basic')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleMembershipSelection('basic');
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Select Basic Member (Free)"
                  >
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={watchedValues.verificationPath === 'basic'}
                          onChange={() => {}}
                          className="pointer-events-none"
                          aria-hidden="true"
                        />
                        <CardTitle className="text-lg">Basic Member</CardTitle>
                        <Badge variant="secondary">Free</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm">
                        <li>✓ Profile in professional directory</li>
                        <li>✓ Basic contact from families</li>
                        <li>✓ Profile management tools</li>
                        <li>✓ Community access</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Step 2: Build Profile */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        {...form.register('firstName')}
                        placeholder="Your first name"
                      />
                      {form.formState.errors.firstName && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.firstName.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        {...form.register('lastName')}
                        placeholder="Your last name"
                      />
                      {form.formState.errors.lastName && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.lastName.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="preferredName">Preferred Name</Label>
                      <Input
                        id="preferredName"
                        {...form.register('preferredName')}
                        placeholder="What you'd like to be called"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        {...form.register('email')}
                        placeholder="your.email@example.com"
                      />
                      {form.formState.errors.email && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        {...form.register('phone')}
                        placeholder="(555) 123-4567"
                      />
                      {form.formState.errors.phone && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.phone.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="experienceYears">Years of Experience *</Label>
                      <Select
                        value={watchedValues.experienceYears}
                        onValueChange={(value) => form.setValue('experienceYears', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPERIENCE_OPTIONS.map((exp) => (
                            <SelectItem key={exp} value={exp}>{exp}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="bioShort">Short Bio *</Label>
                    <Input
                      id="bioShort"
                      {...form.register('bioShort')}
                      placeholder="Brief description of your caregiving approach"
                    />
                    {form.formState.errors.bioShort && (
                      <p className="text-sm text-red-500 mt-1">
                        {form.formState.errors.bioShort.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="languages">Languages *</Label>
                    <Select
                      value={watchedValues.languages?.[0] || ''}
                      onValueChange={(value) => {
                        const current = form.getValues('languages') || [];
                        if (value && !current.includes(value)) {
                          form.setValue('languages', [...current, value]);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select primary language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map(lang => (
                          <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {watchedValues.languages && watchedValues.languages.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {watchedValues.languages.map((lang, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {lang}
                            <button
                              type="button"
                              onClick={() => {
                                const current = form.getValues('languages');
                                form.setValue('languages', current.filter(l => l !== lang));
                              }}
                              className="ml-1 text-muted-foreground hover:text-foreground"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Select languages you speak fluently. Add multiple if applicable.
                    </p>
                    {form.formState.errors.languages && (
                      <p className="text-sm text-red-500 mt-1">
                        {form.formState.errors.languages.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="hourlyRate">Hourly Rate</Label>
                    <Select
                      value={watchedValues.hourlyRate || ''}
                      onValueChange={(value) => form.setValue('hourlyRate', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select hourly rate range" />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURLY_RATE_OPTIONS.map(rate => (
                          <SelectItem key={rate.value} value={rate.value}>{rate.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Optional: Your preferred rate range
                    </p>
                  </div>
                </div>

                {/* Education & Credentials Section */}
                <div className="rounded-lg border p-4 space-y-4">
                  <h3 className="text-base font-semibold">Education and Credentials</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="educationLevel">Highest Education Level *</Label>
                      <Select
                        value={watchedValues.educationLevel}
                        onValueChange={(value: any) => form.setValue('educationLevel', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EDUCATION_LEVELS.map((level) => (
                            <SelectItem key={level.value} value={level.value}>
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.educationLevel && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.educationLevel.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="vaccinationStatus">Vaccination Status</Label>
                      <Select
                        value={watchedValues.vaccinationStatus}
                        onValueChange={(value: any) => form.setValue('vaccinationStatus', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="up_to_date">Up to date</SelectItem>
                          <SelectItem value="partial">Partially vaccinated</SelectItem>
                          <SelectItem value="not_current">Not current</SelectItem>
                          <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Dynamic Education Entries */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">Education Details</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addEducation}
                        className="flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Education
                      </Button>
                    </div>
                    
                    {watchedValues.educationList?.length === 0 && (
                      <p className="text-sm text-muted-foreground mb-2">
                        Add degrees, certifications, or training programs to help families understand your qualifications.
                      </p>
                    )}

                    <div className="space-y-3">
                      {watchedValues.educationList?.map((entry, index) => (
                        <div key={index} className="grid grid-cols-[1fr,1fr,auto,auto] gap-3 items-end p-3 border rounded-lg">
                          <div>
                            <Label htmlFor={`degree-${index}`}>Degree/Program</Label>
                            <Input
                              id={`degree-${index}`}
                              value={entry.degree}
                              onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                              placeholder="Associate in Nursing"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`institution-${index}`}>Institution</Label>
                            <Input
                              id={`institution-${index}`}
                              value={entry.institution}
                              onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                              placeholder="City College"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`year-${index}`}>Year</Label>
                            <Input
                              id={`year-${index}`}
                              value={entry.year}
                              onChange={(e) => updateEducation(index, 'year', e.target.value)}
                              placeholder="2023"
                              className="w-20"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeEducation(index)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Certifications */}
                  <div>
                    <Label className="text-sm font-medium">Professional Certifications</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {CERTIFICATION_OPTIONS.map((cert) => (
                        <div key={cert} className="flex items-center space-x-2">
                          <Checkbox
                            id={`cert-${cert}`}
                            checked={watchedValues.certifications?.includes(cert)}
                            onCheckedChange={(checked) => handleCertificationToggle(cert, checked as boolean)}
                          />
                          <Label htmlFor={`cert-${cert}`} className="text-sm cursor-pointer">
                            {cert}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Vaccinations */}
                  <div>
                    <Label className="text-sm font-medium">Current Vaccinations</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {VACCINE_OPTIONS.map((vaccine) => (
                        <div key={vaccine} className="flex items-center space-x-2">
                          <Checkbox
                            id={`vaccine-${vaccine}`}
                            checked={watchedValues.vaccinationsCurrent?.includes(vaccine)}
                            onCheckedChange={(checked) => handleVaccinationToggle(vaccine, checked as boolean)}
                          />
                          <Label htmlFor={`vaccine-${vaccine}`} className="text-sm cursor-pointer">
                            {vaccine}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Professional Credentials */}
                  <div>
                    <Label className="text-sm font-medium">Additional Credentials</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {CREDENTIAL_OPTIONS.map((cred) => (
                        <div key={cred} className="flex items-center space-x-2">
                          <Checkbox
                            id={`cred-${cred}`}
                            checked={watchedValues.professionalCredentials?.includes(cred)}
                            onCheckedChange={(checked) => handleCredentialToggle(cred, checked as boolean)}
                          />
                          <Label htmlFor={`cred-${cred}`} className="text-sm cursor-pointer">
                            {cred}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tools Used - Competitive Intelligence */}
                <div className="pt-4 border-t">
                  <Label className="text-sm font-medium mb-2 block">
                    Which tools do you currently use to manage caregiving tasks?
                  </Label>
                  <p className="text-xs text-muted-foreground mb-4">
                    Select all that apply. This helps us understand your current workflow and how we can best support you.
                  </p>
                  
                  <div className="space-y-4">
                    {['📝 Everyday Tools', '💼 Professional Caregiver Platforms', '🏡 Family Caregiver Apps', '❓ Other or None'].map((category) => {
                      const categoryTools = TOOLS_OPTIONS.filter(tool => tool.category === category);
                      return (
                        <div key={category} className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">{category}</p>
                          <div className="grid grid-cols-2 gap-2">
                            {categoryTools.map((tool) => (
                              <div key={tool.value} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`tool-${tool.value}`}
                                  checked={watchedValues.toolsUsed?.includes(tool.value)}
                                  onCheckedChange={(checked) => handleToolToggle(tool.value, checked as boolean)}
                                />
                                <Label htmlFor={`tool-${tool.value}`} className="text-sm cursor-pointer">
                                  {tool.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Other tool input */}
                  {watchedValues.toolsUsed?.includes('other') && (
                    <div className="mt-4">
                      <Label htmlFor="tools-other" className="text-sm">Please specify other tools</Label>
                      <Input
                        id="tools-other"
                        {...form.register('toolsOther')}
                        placeholder="Enter other caregiving tools you use"
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>

                {/* Profile Photo - Highlighted Section */}
                <div className="p-6 bg-primary/5 border-2 border-primary/20 rounded-lg">
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-primary" />
                    Profile Photo
                  </h4>
                  <div className="grid grid-cols-2 gap-4 items-end">
                    <div>
                      <Label>Current Photo</Label>
                      {watchedValues.professionalPhoto ? (
                        <div className="mt-2">
                          <img
                            src={watchedValues.professionalPhoto}
                            alt="Profile preview"
                            className="h-24 w-24 rounded-full object-cover border-2 border-primary/30"
                          />
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground mt-2">
                          Add a professional headshot to help families connect with you.
                        </p>
                      )}
                    </div>
                    <div>
                    <Label htmlFor="photo-upload">Upload Photo</Label>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        const { valid, error } = validateFile(file);
                        if (!valid) {
                          toast.error(error || 'Invalid file');
                          e.target.value = '';
                          return;
                        }

                        const uploadFile = async (retryCount = 0): Promise<boolean> => {
                          try {
                            setIsUploading(true);
                            
                            
                            // Generate simple, reliable file path
                            const timestamp = Date.now();
                            const randomStr = Math.random().toString(36).substring(2, 8);
                            const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
                            const filePath = `profiles/${timestamp}_${randomStr}.${extension}`;
                            
                            console.log(`Uploading file to: ${filePath}`, {
                              fileName: file.name,
                              fileSize: file.size,
                              fileType: file.type
                            });
                            
                            // Attempt upload
                            const { data: uploadData, error: uploadError } = await supabase.storage
                              .from('caregiver-profiles')
                              .upload(filePath, file, { 
                                upsert: false,  // Don't attempt updates - only inserts for unauthenticated users
                                contentType: file.type 
                              });
                            
                            if (uploadError) {
                              console.error('Upload error details:', {
                                message: uploadError.message,
                                status: (uploadError as any).status,
                                statusCode: (uploadError as any).statusCode,
                                details: (uploadError as any).details,
                                hint: (uploadError as any).hint,
                                code: (uploadError as any).code,
                                fullError: uploadError
                              });
                              
                              // Retry logic
                              if (retryCount < 1) {
                                console.log(`Upload failed, retrying in 1 second... (attempt ${retryCount + 1}/2)`);
                                toast.error('Upload failed, retrying...');
                                await new Promise(resolve => setTimeout(resolve, 1000));
                                return uploadFile(retryCount + 1);
                              }
                              
                              // Format error message
                              let errorMsg = uploadError.message || 'Unknown error';
                              if ((uploadError as any).statusCode === 403) {
                                errorMsg = 'Permission denied - storage policy issue';
                              } else if ((uploadError as any).statusCode === 413) {
                                errorMsg = 'File too large';
                              } else if ((uploadError as any).statusCode >= 500) {
                                errorMsg = 'Server error - please try again';
                              }
                              
                              toast.error(`Upload failed: ${errorMsg}`);
                              return false;
                            }
                            
                            console.log('Upload successful:', uploadData);
                            
                            // Get public URL
                            const { data: pub } = supabase.storage
                              .from('caregiver-profiles')
                              .getPublicUrl(filePath);
                            
                            form.setValue('professionalPhoto', pub.publicUrl);
                            toast.success(`Profile photo uploaded: ${file.name}`);
                            return true;
                            
                          } catch (err: any) {
                            console.error('Unexpected error during upload:', {
                              message: err?.message,
                              status: err?.status,
                              statusCode: err?.statusCode,
                              details: err?.details,
                              hint: err?.hint,
                              code: err?.code,
                              name: err?.name,
                              stack: err?.stack,
                              fullError: err
                            });
                            
                            // Retry on unexpected errors
                            if (retryCount < 1) {
                              console.log(`Unexpected error, retrying in 1 second... (attempt ${retryCount + 1}/2)`);
                              toast.error('Network error, retrying...');
                              await new Promise(resolve => setTimeout(resolve, 1000));
                              return uploadFile(retryCount + 1);
                            }
                            
                            const errorMsg = err?.message || err?.error_description || err?.error || 'An unexpected error occurred';
                            toast.error(`Upload error: ${errorMsg}`);
                            return false;
                          } finally {
                            setIsUploading(false);
                          }
                        };

                        const success = await uploadFile();
                        e.target.value = '';
                      }}
                      disabled={isUploading}
                      className="block w-full text-sm text-muted-foreground
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-primary-foreground
                      hover:file:bg-primary/80"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      JPG, PNG, WEBP up to 5MB
                    </p>
                  </div>
                </div>
                </div>
              </div>
            )}

            {/* Step 3: Services & Skills */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-medium">Services You Offer *</Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Select the services you can provide to help families understand your capabilities:
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {SERVICE_OPTIONS.map((service) => (
                      <div key={service} className="flex items-center space-x-2">
                        <Checkbox
                          id={`service-${service}`}
                          checked={watchedValues.services?.includes(service)}
                          onCheckedChange={(checked) => handleServiceToggle(service, checked as boolean)}
                        />
                        <Label htmlFor={`service-${service}`} className="text-sm cursor-pointer">
                          {service}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {form.formState.errors.services && (
                    <p className="text-sm text-red-500 mt-1">
                      {form.formState.errors.services.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-base font-medium">Comfort Levels</Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Let families know about your comfort working in different environments:
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {COMFORT_OPTIONS.map((comfort) => (
                      <div key={comfort} className="flex items-center space-x-2">
                        <Checkbox
                          id={`comfort-${comfort}`}
                          checked={watchedValues.comfort?.includes(comfort)}
                          onCheckedChange={(checked) => handleComfortToggle(comfort, checked as boolean)}
                        />
                        <Label htmlFor={`comfort-${comfort}`} className="text-sm cursor-pointer">
                          {comfort}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-base font-medium">Specialized Care Skills</Label>
                  <div className="grid grid-cols-1 gap-3 mt-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="dementia-care"
                        checked={watchedValues.skillsDementiaCare}
                        onCheckedChange={(checked) => form.setValue('skillsDementiaCare', checked as boolean)}
                      />
                      <Label htmlFor="dementia-care" className="cursor-pointer">
                        Dementia & Alzheimer's Care
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="mobility-assistance"
                        checked={watchedValues.skillsMobilityAssistance}
                        onCheckedChange={(checked) => form.setValue('skillsMobilityAssistance', checked as boolean)}
                      />
                      <Label htmlFor="mobility-assistance" className="cursor-pointer">
                        Mobility Assistance & Transfer Support
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="medication-support"
                        checked={watchedValues.skillsMedicationSupport}
                        onCheckedChange={(checked) => form.setValue('skillsMedicationSupport', checked as boolean)}
                      />
                      <Label htmlFor="medication-support" className="cursor-pointer">
                        Medication Reminders & Support
                      </Label>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-base font-medium">Work Preferences</Label>
                  <div className="grid grid-cols-1 gap-3 mt-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="has-transportation"
                        checked={watchedValues.hasTransportation}
                        onCheckedChange={(checked) => form.setValue('hasTransportation', checked as boolean)}
                      />
                      <Label htmlFor="has-transportation" className="cursor-pointer">
                        I have reliable transportation
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="comfortable-pets"
                        checked={watchedValues.comfortableWithPets}
                        onCheckedChange={(checked) => form.setValue('comfortableWithPets', checked as boolean)}
                      />
                      <Label htmlFor="comfortable-pets" className="cursor-pointer">
                        I'm comfortable working around pets
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="willing-housekeeping"
                        checked={watchedValues.willingHousekeeping}
                        onCheckedChange={(checked) => form.setValue('willingHousekeeping', checked as boolean)}
                      />
                      <Label htmlFor="willing-housekeeping" className="cursor-pointer">
                        I'm willing to do light housekeeping
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Availability & Service Area */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-medium">Service Area</Label>
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div>
                      <Label htmlFor="service-city">Primary City *</Label>
                      <Input
                        id="service-city"
                        {...form.register('serviceCity')}
                        placeholder="Beverly Hills"
                      />
                      {form.formState.errors.serviceCity && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.serviceCity.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="service-zip">Home Base ZIP *</Label>
                      <Input
                        id="service-zip"
                        {...form.register('serviceZipCode')}
                        placeholder="90210"
                      />
                      {form.formState.errors.serviceZipCode && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.serviceZipCode.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="service-radius">Travel Radius (miles)</Label>
                      <Select
                        value={String(watchedValues.serviceRadius)}
                        onValueChange={(value) => form.setValue('serviceRadius', Number(value))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5 miles</SelectItem>
                          <SelectItem value="10">10 miles</SelectItem>
                          <SelectItem value="15">15 miles</SelectItem>
                          <SelectItem value="20">20 miles</SelectItem>
                          <SelectItem value="25">25 miles</SelectItem>
                          <SelectItem value="30">30 miles</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="caregiver-type">Caregiver Type *</Label>
                    <Select
                      value={watchedValues.caregiverType}
                      onValueChange={(value: 'independent' | 'agency') => form.setValue('caregiverType', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="independent">Independent Caregiver</SelectItem>
                        <SelectItem value="agency">Agency Caregiver</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="availability-hours">Availability Hours *</Label>
                    <Select
                      value={watchedValues.availabilityHours}
                      onValueChange={(value: 'full-time' | 'part-time' | 'flexible') => form.setValue('availabilityHours', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full-time">Full-time (40+ hours)</SelectItem>
                        <SelectItem value="part-time">Part-time (20-39 hours)</SelectItem>  
                        <SelectItem value="flexible">Flexible Schedule</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-base font-medium">Available Days *</Label>
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {AVAILABILITY_DAYS.map((day) => (
                      <div key={day} className="flex items-center space-x-2">
                        <Checkbox
                          id={`day-${day}`}
                          checked={watchedValues.availabilityDays?.includes(day)}
                          onCheckedChange={(checked) => handleDayToggle(day, checked as boolean)}
                        />
                        <Label htmlFor={`day-${day}`} className="text-sm cursor-pointer capitalize">
                          {day}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {form.formState.errors.availabilityDays && (
                    <p className="text-sm text-red-500 mt-1">
                      {form.formState.errors.availabilityDays.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="overnight"
                      checked={watchedValues.overnight}
                      onCheckedChange={(checked) => form.setValue('overnight', checked as boolean)}
                    />
                    <Label htmlFor="overnight" className="cursor-pointer">
                      Overnight care
                    </Label>
                  </div>
                  <div>
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      {...form.register('startDate')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="max-hours">Max hours/week</Label>
                    <Input
                      id="max-hours"
                      {...form.register('maxWeeklyHours')}
                      placeholder="40"
                    />
                  </div>
                  <div>
                    <Label htmlFor="preferred-shift">Preferred shift length</Label>
                    <Select
                      value={watchedValues.preferredShift}
                      onValueChange={(value) => form.setValue('preferredShift', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2 to 4 hours">2 to 4 hours</SelectItem>
                        <SelectItem value="4 to 6 hours">4 to 6 hours</SelectItem>
                        <SelectItem value="6 to 8 hours">6 to 8 hours</SelectItem>
                        <SelectItem value="8 plus">8+ hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Review & Submit */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold mb-2">Review and Submit</h3>
                  <p className="text-muted-foreground">
                    One last check before we submit your application
                  </p>
                </div>

                {/* Application Summary */}
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Profile Card with Photo */}
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20 p-6 shadow-sm">
                    <div className="flex items-start gap-4 mb-4">
                      {watchedValues.professionalPhoto ? (
                        <img
                          src={watchedValues.professionalPhoto}
                          alt="Profile"
                          className="w-16 h-16 rounded-full object-cover border-2 border-primary/20"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center border-2 border-primary/20">
                          <Star className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-bold text-lg text-foreground mb-1">
                          {watchedValues.firstName} {watchedValues.lastName}
                        </h4>
                        {watchedValues.preferredName && (
                          <p className="text-sm text-muted-foreground mb-1">"{watchedValues.preferredName}"</p>
                        )}
                        <div className="flex items-center gap-2">
                          <Badge variant={isTrustedPro ? "default" : "secondary"} className="text-xs">
                            {isTrustedPro ? 'Trusted Pro' : 'Basic Member'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground font-medium">Experience:</dt>
                        <dd className="font-semibold">{watchedValues.experienceYears} years</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground font-medium">Languages:</dt>
                        <dd className="font-semibold text-right">{watchedValues.languages?.join(', ') || 'Not specified'}</dd>
                      </div>
                      {watchedValues.hourlyRate && (
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground font-medium">Rate:</dt>
                          <dd className="font-semibold">{watchedValues.hourlyRate}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {/* Membership Details */}
                  <div className="bg-gradient-to-br from-accent/5 to-accent/10 rounded-xl border border-accent/20 p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-accent/20">
                        <Shield className="w-5 h-5 text-accent-foreground" />
                      </div>
                      <h4 className="font-bold text-lg">Membership Plan</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Selected Plan:</span>
                        <Badge variant={isTrustedPro ? "default" : "secondary"} className="font-semibold">
                          {isTrustedPro ? 'Trusted Pro' : 'Basic Member'}
                        </Badge>
                      </div>
                      {isTrustedPro && (
                        <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
                          <p className="text-sm text-primary-foreground font-medium">Premium Benefits Included:</p>
                          <ul className="text-xs text-primary-foreground/80 mt-1 space-y-1">
                            <li>• Background check verification</li>
                            <li>• Priority search placement</li>
                            <li>• Trusted Pro badge</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Education & Credentials */}
                  <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 rounded-xl border border-secondary/20 p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-secondary/20">
                        <Award className="w-5 h-5 text-secondary-foreground" />
                      </div>
                      <h4 className="font-bold text-lg">Education & Credentials</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <span className="text-muted-foreground font-medium">Education:</span>
                        <span className="font-semibold text-right">
                          {EDUCATION_LEVELS.find(l => l.value === watchedValues.educationLevel)?.label || 'Not specified'}
                        </span>
                      </div>
                      {watchedValues.certifications && watchedValues.certifications.length > 0 && (
                        <div className="flex justify-between items-start">
                          <span className="text-muted-foreground font-medium">Certifications:</span>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {watchedValues.certifications.map((cert, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{cert}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {watchedValues.professionalCredentials && watchedValues.professionalCredentials.length > 0 && (
                        <div className="flex justify-between items-start">
                          <span className="text-muted-foreground font-medium">Credentials:</span>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {watchedValues.professionalCredentials.map((cred, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{cred}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground font-medium">Vaccination:</span>
                        <span className="font-semibold capitalize">
                          {watchedValues.vaccinationStatus?.replace('_', ' ') || 'Not specified'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Services & Skills */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/40">
                        <Check className="w-5 h-5 text-green-700 dark:text-green-300" />
                      </div>
                      <h4 className="font-bold text-lg">Services & Skills</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-muted-foreground font-medium block mb-2">Services Offered:</span>
                        <div className="flex flex-wrap gap-1">
                          {watchedValues.services?.map((service, i) => (
                            <Badge key={i} className="text-xs bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800">
                              {service}
                            </Badge>
                          )) || <span className="text-sm text-muted-foreground">None specified</span>}
                        </div>
                      </div>
                      
                      {[
                        watchedValues.skillsDementiaCare && 'Dementia Care',
                        watchedValues.skillsMobilityAssistance && 'Mobility Assistance', 
                        watchedValues.skillsMedicationSupport && 'Medication Support'
                      ].filter(Boolean).length > 0 && (
                        <div>
                          <span className="text-muted-foreground font-medium block mb-2">Specialties:</span>
                          <div className="flex flex-wrap gap-1">
                            {[
                              watchedValues.skillsDementiaCare && 'Dementia Care',
                              watchedValues.skillsMobilityAssistance && 'Mobility Assistance',
                              watchedValues.skillsMedicationSupport && 'Medication Support'
                            ].filter(Boolean).map((specialty, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {specialty}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {[
                        watchedValues.hasTransportation && 'Has Transportation',
                        watchedValues.comfortableWithPets && 'Pet-Friendly', 
                        watchedValues.willingHousekeeping && 'Light Housekeeping'
                      ].filter(Boolean).length > 0 && (
                        <div>
                          <span className="text-muted-foreground font-medium block mb-2">Additional Services:</span>
                          <div className="flex flex-wrap gap-1">
                            {[
                              watchedValues.hasTransportation && 'Has Transportation',
                              watchedValues.comfortableWithPets && 'Pet-Friendly',
                              watchedValues.willingHousekeeping && 'Light Housekeeping'
                            ].filter(Boolean).map((pref, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {pref}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Availability & Area */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6 shadow-sm col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
                        <Star className="w-5 h-5 text-blue-700 dark:text-blue-300" />
                      </div>
                      <h4 className="font-bold text-lg">Availability & Service Area</h4>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-3">
                        <div>
                          <span className="text-muted-foreground font-medium block mb-1">Service Type:</span>
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800">
                            {watchedValues.caregiverType === 'independent' ? 'Independent Caregiver' : 'Agency Caregiver'}
                          </Badge>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-medium block mb-1">Location:</span>
                          <span className="font-semibold">{watchedValues.serviceCity}, {watchedValues.serviceZipCode}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-medium block mb-1">Service Radius:</span>
                          <span className="font-semibold">{watchedValues.serviceRadius} miles</span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <span className="text-muted-foreground font-medium block mb-2">Available Days:</span>
                          <div className="flex flex-wrap gap-1">
                            {watchedValues.availabilityDays?.map((day, i) => (
                              <Badge key={i} variant="outline" className="text-xs capitalize">
                                {day.slice(0, 3)}
                              </Badge>
                            )) || <span className="text-sm text-muted-foreground">None specified</span>}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-medium block mb-1">Schedule Type:</span>
                          <span className="font-semibold capitalize">{watchedValues.availabilityHours?.replace('-', ' ')}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        {watchedValues.overnight && (
                          <div>
                            <span className="text-muted-foreground font-medium block mb-1">Special Availability:</span>
                            <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-800">
                              Overnight Care Available
                            </Badge>
                          </div>
                        )}
                        {watchedValues.maxWeeklyHours && (
                          <div>
                            <span className="text-muted-foreground font-medium block mb-1">Max Weekly Hours:</span>
                            <span className="font-semibold">{watchedValues.maxWeeklyHours} hours</span>
                          </div>
                        )}
                        {watchedValues.preferredShift && (
                          <div>
                            <span className="text-muted-foreground font-medium block mb-1">Preferred Shift:</span>
                            <span className="font-semibold">{watchedValues.preferredShift}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Security Notice */}
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-xl border border-blue-200 dark:border-blue-800 text-sm">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex-shrink-0">
                    <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      {isTrustedPro ? 'Secure Background Check Process' : 'Privacy & Security'}
                    </h5>
                    <span className="text-blue-800 dark:text-blue-200">
                      {isTrustedPro 
                        ? 'Your background check will be completed through our secure partner portal. SeniorThrive never stores SSN or date of birth.'
                        : 'Your information is secure and will only be shared with potential care recipients with your consent.'
                      }
                    </span>
                  </div>
                </div>

                {/* Agreement */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="terms-agreed"
                      checked={watchedValues.termsAgreed}
                      onCheckedChange={(checked) => form.setValue('termsAgreed', checked as boolean)}
                    />
                    <Label htmlFor="terms-agreed" className="cursor-pointer">
                      I agree to the SeniorThrive Terms of Service and Privacy Policy *
                    </Label>
                  </div>
                  
                  {isTrustedPro && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="background-check-consent"
                        checked={watchedValues.backgroundCheckConsent}
                        onCheckedChange={(checked) => form.setValue('backgroundCheckConsent', checked as boolean)}
                      />
                      <Label htmlFor="background-check-consent" className="cursor-pointer">
                        I consent to a background check as part of the Trusted Pro verification process
                      </Label>
                    </div>
                  )}
                </div>

                {form.formState.errors.termsAgreed && (
                  <p className="text-sm text-red-500">
                    {form.formState.errors.termsAgreed.message}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Button>

          {currentStep === 1 ? (
            <p className="text-sm text-muted-foreground self-center">
              Click a membership option to continue
            </p>
          ) : currentStep < STEPS.length ? (
            <Button
              type="button"
              onClick={nextStep}
              className="flex items-center gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isSubmitting || !watchedValues.termsAgreed || (isTrustedPro && !watchedValues.backgroundCheckConsent)}
              className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg disabled:from-gray-400 disabled:to-gray-500"
              onClick={() => {
                console.log('Submit button clicked:', {
                  isSubmitting,
                  termsAgreed: watchedValues.termsAgreed,
                  isTrustedPro,
                  backgroundCheckConsent: watchedValues.backgroundCheckConsent,
                  disabled: isSubmitting || !watchedValues.termsAgreed || (isTrustedPro && !watchedValues.backgroundCheckConsent)
                });
              }}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Submitting Application...
                </>
              ) : (
                <>
                  Submit Application
                  <Check className="w-4 h-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}